/**
 * Datadog RUM utility module for the Office Add-in.
 *
 * Mirrors the pattern from hai_assistants/ui/lib/datadog.ts.
 * All functions are safe to call even if the SDK is not initialized —
 * they silently no-op when RUM is unavailable.
 */

import type { datadogRum as DatadogRumType } from '@datadog/browser-rum';

let rumInstance: typeof DatadogRumType | null = null;
let sdkUnavailable = false;
let initResolve: ((rum: typeof DatadogRumType) => void) | null = null;
const initPromise = new Promise<typeof DatadogRumType | null>((resolve) => {
  initResolve = (rum) => resolve(rum);
  // Auto-resolve to null after 10s if SDK never initializes
  setTimeout(() => resolve(null), 10000);
});

/**
 * Called by DatadogProvider after successful init to unblock
 * any pending getRumInstance() calls.
 */
export function notifyDatadogReady(): void {
  import('@datadog/browser-rum').then(({ datadogRum }) => {
    if (datadogRum.getInitConfiguration()) {
      rumInstance = datadogRum;
      initResolve?.(datadogRum);
    }
  }).catch(() => { /* ignore */ });
}

async function getRumInstance(): Promise<typeof DatadogRumType | null> {
  if (rumInstance) return rumInstance;
  if (sdkUnavailable) return null;

  // Wait for the SDK to be initialized by DatadogProvider
  const rum = await initPromise;
  if (rum) {
    rumInstance = rum;
    return rum;
  }

  // Fallback: try direct import
  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    if (datadogRum.getInitConfiguration()) {
      rumInstance = datadogRum;
      return rumInstance;
    }
  } catch {
    sdkUnavailable = true;
  }
  return null;
}

// ---------------------------------------------------------------------------
// User Identification
// ---------------------------------------------------------------------------

export async function setDatadogUser(user: {
  id: string;
  email?: string;
  name?: string;
}): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.setUser({ id: user.id, email: user.email, name: user.name });
}

export async function clearDatadogUser(): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.clearUser();
}

// ---------------------------------------------------------------------------
// Global Context
// ---------------------------------------------------------------------------

export async function setDatadogGlobalContext(key: string, value: unknown): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.setGlobalContextProperty(key, value);
}

export async function removeDatadogGlobalContext(key: string): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.removeGlobalContextProperty(key);
}

// ---------------------------------------------------------------------------
// Custom Actions
// ---------------------------------------------------------------------------

export async function addDatadogAction(
  name: string,
  context?: Record<string, unknown>,
): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.addAction(name, context);
}

// ---------------------------------------------------------------------------
// Custom Errors
// ---------------------------------------------------------------------------

export async function addDatadogError(
  error: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  const rum = await getRumInstance();
  if (!rum) return;
  rum.addError(error, context);
}
